import { useCallback, useEffect, useRef, useState } from "react";
import { Loader2, Minus, Plus, RotateCcw } from "lucide-react";

const BOX = 320;
const OUT = 512;

/**
 * Drag-and-zoom cropper for the brand logo. The frame is the same circle used
 * on the contact cards in class, so what the host frames here is exactly what
 * students see. Exports a square PNG (transparency preserved) — the app masks
 * it to a circle, which keeps the source usable if the mask ever changes.
 */
export function LogoCropper({ file, busy, onCancel, onSave }) {
  const [img, setImg] = useState(null);
  const [zoom, setZoom] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const drag = useRef(null);
  const pinch = useRef(null);

  useEffect(() => {
    const url = URL.createObjectURL(file);
    const el = new Image();
    el.onload = () => setImg(el);
    el.src = url;
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const base = img ? BOX / Math.min(img.naturalWidth, img.naturalHeight) : 1;
  const scale = base * zoom;
  const drawW = img ? img.naturalWidth * scale : 0;
  const drawH = img ? img.naturalHeight * scale : 0;

  const clamp = useCallback(
    (o) => ({
      x: Math.max(-Math.max(0, (drawW - BOX) / 2), Math.min(Math.max(0, (drawW - BOX) / 2), o.x)),
      y: Math.max(-Math.max(0, (drawH - BOX) / 2), Math.min(Math.max(0, (drawH - BOX) / 2), o.y)),
    }),
    [drawW, drawH],
  );

  useEffect(() => setOffset((o) => clamp(o)), [clamp]);

  const onPointerDown = (e) => {
    e.currentTarget.setPointerCapture(e.pointerId);
    if (!pinch.current) pinch.current = new Map();
    pinch.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    drag.current = { x: e.clientX, y: e.clientY, ox: offset.x, oy: offset.y };
  };

  const onPointerMove = (e) => {
    if (pinch.current?.size === 2) {
      const pts = [...pinch.current.values()];
      const prev = Math.hypot(pts[0].x - pts[1].x, pts[0].y - pts[1].y);
      pinch.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
      const now = [...pinch.current.values()];
      const dist = Math.hypot(now[0].x - now[1].x, now[0].y - now[1].y);
      if (prev > 0) setZoom((z) => Math.min(4, Math.max(1, z * (dist / prev))));
      return;
    }
    if (!drag.current) return;
    setOffset(clamp({ x: drag.current.ox + (e.clientX - drag.current.x), y: drag.current.oy + (e.clientY - drag.current.y) }));
  };

  const endPointer = (e) => {
    pinch.current?.delete(e.pointerId);
    drag.current = null;
  };

  const save = () => {
    const canvas = document.createElement("canvas");
    canvas.width = OUT;
    canvas.height = OUT;
    const ctx = canvas.getContext("2d");
    const r = OUT / BOX;
    ctx.drawImage(
      img,
      r * (BOX / 2 - drawW / 2 + offset.x),
      r * (BOX / 2 - drawH / 2 + offset.y),
      drawW * r,
      drawH * r,
    );
    canvas.toBlob((blob) => blob && onSave(blob), "image/png");
  };

  return (
    <div className="fixed inset-0 z-[80] bg-black/80 backdrop-blur-sm flex items-center justify-center px-5 py-8 overflow-y-auto" data-testid="logo-cropper">
      <div className="w-full max-w-[400px] rounded-3xl bg-[#0c0c0c] ring-1 ring-white/10 p-6">
        <div className="text-[10.5px] uppercase tracking-[0.22em] text-emerald-400/80 mb-1.5">Frame your logo</div>
        <h2 className="font-display text-xl font-semibold text-white mb-1">Drag to move, pinch or slide to zoom</h2>
        <p className="text-[12px] text-white/40 mb-6">This circle is exactly how it appears on your contact card in class.</p>

        <div
          className="relative mx-auto rounded-full overflow-hidden bg-neutral-950 ring-1 ring-emerald-500/40 touch-none cursor-grab active:cursor-grabbing"
          style={{ width: BOX, height: BOX }}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={endPointer}
          onPointerCancel={endPointer}
          onWheel={(e) => {
            e.preventDefault();
            setZoom((z) => Math.min(4, Math.max(1, z - e.deltaY * 0.0015)));
          }}
          data-testid="logo-crop-frame"
        >
          {img ? (
            <img
              src={img.src}
              alt=""
              draggable={false}
              className="absolute select-none"
              style={{
                width: drawW,
                height: drawH,
                // Tailwind preflight sets img { max-width: 100% } which would
                // clamp a zoomed-in logo back to the frame width.
                maxWidth: "none",
                maxHeight: "none",
                left: BOX / 2 - drawW / 2 + offset.x,
                top: BOX / 2 - drawH / 2 + offset.y,
              }}
            />
          ) : (
            <div className="absolute inset-0 grid place-items-center">
              <Loader2 className="w-5 h-5 text-white/30 animate-spin" />
            </div>
          )}
        </div>

        <div className="flex items-center gap-3 mt-6">
          <button
            onClick={() => setZoom((z) => Math.max(1, z - 0.2))}
            className="w-9 h-9 shrink-0 grid place-items-center rounded-full ring-1 ring-white/12 text-white/60 hover:text-white hover:ring-white/30 transition-colors"
            data-testid="logo-crop-zoom-out"
            aria-label="Zoom out"
          >
            <Minus className="w-4 h-4" />
          </button>
          <input
            type="range"
            min="1"
            max="4"
            step="0.01"
            value={zoom}
            onChange={(e) => setZoom(Number(e.target.value))}
            className="flex-1 accent-emerald-400"
            data-testid="logo-crop-zoom"
            aria-label="Zoom"
          />
          <button
            onClick={() => setZoom((z) => Math.min(4, z + 0.2))}
            className="w-9 h-9 shrink-0 grid place-items-center rounded-full ring-1 ring-white/12 text-white/60 hover:text-white hover:ring-white/30 transition-colors"
            data-testid="logo-crop-zoom-in"
            aria-label="Zoom in"
          >
            <Plus className="w-4 h-4" />
          </button>
          <button
            onClick={() => {
              setZoom(1);
              setOffset({ x: 0, y: 0 });
            }}
            className="w-9 h-9 shrink-0 grid place-items-center rounded-full ring-1 ring-white/12 text-white/60 hover:text-white hover:ring-white/30 transition-colors"
            data-testid="logo-crop-reset"
            aria-label="Reset"
          >
            <RotateCcw className="w-3.5 h-3.5" />
          </button>
        </div>

        <div className="flex gap-3 mt-7">
          <button
            onClick={onCancel}
            disabled={busy}
            className="flex-1 h-11 rounded-full ring-1 ring-white/12 text-[12.5px] text-white/60 hover:text-white hover:ring-white/25 transition-colors disabled:opacity-50"
            data-testid="logo-crop-cancel"
          >
            Cancel
          </button>
          <button
            onClick={save}
            disabled={busy || !img}
            className="flex-1 h-11 rounded-full bg-emerald-400 text-black text-[13px] font-semibold inline-flex items-center justify-center gap-2 transition-transform duration-200 hover:-translate-y-0.5 disabled:opacity-50"
            data-testid="logo-crop-save"
          >
            {busy && <Loader2 className="w-4 h-4 animate-spin" />}
            SAVE LOGO
          </button>
        </div>
      </div>
    </div>
  );
}
